import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Mail, UserCircle2, LogOut } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'ao_admin_token';

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || '');
  const [input, setInput] = useState('');
  const [subs, setSubs] = useState([]);
  const [msgs, setMsgs] = useState([]);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const load = async (t) => {
    setLoading(true); setErr('');
    try {
      const headers = { Authorization: `Bearer ${t}` };
      const [s, m] = await Promise.all([
        axios.get(`${API}/admin/subscribers`, { headers }),
        axios.get(`${API}/admin/messages`, { headers }),
      ]);
      setSubs(s.data); setMsgs(m.data);
    } catch (e) {
      setErr(e?.response?.status === 401 ? 'Invalid admin token.' : 'Could not load.');
      setToken(''); localStorage.removeItem(TOKEN_KEY);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (token) load(token); }, [token]);

  const signIn = (e) => { e.preventDefault(); localStorage.setItem(TOKEN_KEY, input); setToken(input); };
  const signOut = () => { localStorage.removeItem(TOKEN_KEY); setToken(''); setSubs([]); setMsgs([]); };

  if (!token) {
    return (
      <div className="fade-in mx-auto max-w-md px-5 py-20 lg:px-8">
        <h1 className="text-center font-display text-5xl">Admin</h1>
        <p className="mt-3 text-center text-espresso/60">Enter your admin token to view the inbox.</p>
        <form onSubmit={signIn} className="mt-10 space-y-3">
          <input autoFocus type="password" required value={input} onChange={(e) => setInput(e.target.value)} placeholder="Admin token" className="w-full border border-stone-warm bg-white px-4 py-3 outline-none focus:border-gold" />
          <button className="btn btn-dark w-full">Sign in</button>
        </form>
        {err && <p className="mt-4 text-center text-sm text-destructive">{err}</p>}
      </div>
    );
  }

  return (
    <div className="fade-in mx-auto max-w-6xl px-5 py-16 lg:px-8">
      <div className="flex items-end justify-between">
        <div>
          <p className="eyebrow text-espresso/60">Admin inbox</p>
          <h1 className="mt-1 font-display text-5xl">Hello.</h1>
        </div>
        <button onClick={signOut} className="inline-flex items-center gap-2 text-xs tracking-[0.18em] text-espresso hover:text-gold"><LogOut className="size-4" /> SIGN OUT</button>
      </div>
      {loading && <p className="mt-6 text-espresso/60">Loading…</p>}

      <section className="mt-10">
        <div className="flex items-center gap-2"><Mail className="size-4 text-gold" /><h2 className="font-display text-2xl">Contact messages ({msgs.length})</h2></div>
        <div className="mt-4 overflow-hidden border border-stone-warm bg-white">
          <table className="w-full text-sm">
            <thead className="bg-champagne text-left"><tr><th className="px-4 py-3">Date</th><th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Order</th><th className="px-4 py-3">Message</th></tr></thead>
            <tbody>
              {msgs.length === 0 ? <tr><td colSpan="5" className="px-4 py-6 text-center text-espresso/50">No messages yet.</td></tr> :
                msgs.map((m) => (
                  <tr key={m.id} className="border-t border-stone-warm">
                    <td className="px-4 py-3 text-espresso/70">{new Date(m.created_at).toLocaleString()}</td>
                    <td className="px-4 py-3">{m.name}</td>
                    <td className="px-4 py-3"><a href={`mailto:${m.email}`} className="text-gold hover:underline">{m.email}</a></td>
                    <td className="px-4 py-3 text-espresso/70">{m.order_number || '—'}</td>
                    <td className="px-4 py-3 max-w-md">{m.message}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mt-14">
        <div className="flex items-center gap-2"><UserCircle2 className="size-4 text-gold" /><h2 className="font-display text-2xl">Subscribers ({subs.length})</h2></div>
        <div className="mt-4 overflow-hidden border border-stone-warm bg-white">
          <table className="w-full text-sm">
            <thead className="bg-champagne text-left"><tr><th className="px-4 py-3">Date</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Source</th></tr></thead>
            <tbody>
              {subs.length === 0 ? <tr><td colSpan="3" className="px-4 py-6 text-center text-espresso/50">No subscribers yet.</td></tr> :
                subs.map((s) => (
                  <tr key={s.id} className="border-t border-stone-warm">
                    <td className="px-4 py-3 text-espresso/70">{new Date(s.created_at).toLocaleString()}</td>
                    <td className="px-4 py-3">{s.email}</td>
                    <td className="px-4 py-3 text-espresso/70">{s.source}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
